import { useEffect, useMemo, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import { Button, ViewsNav, type Navigate } from "@omnabase/ui";
import type { Tag } from "@omnabase/shared";
import { useCustomersSidebar } from "../../lib/sidebar";
import { useSetParam } from "../../lib/nav";
import { toggleFolded } from "../../lib/sidebarEdits";
import { CustomizeSidebar } from "../inbox/CustomizeSidebar";
import { customersArea, customersViewGroups, type CustomersColumnInput } from "./customersViews";

/** The Customers screen's views column as the person arranged it (the customers sidebar round, 2026-10-29): the same column on the list and
 *  on a customer's own page. A heading folds its group (saved with the layout); "Customize" at the column's foot opens the inbox's dialog
 *  (`?customize=1`) with the Customers column's words and pool, and the column follows it live. After a save the list is read again, so a
 *  newly shown entry gets its number. */
export function CustomersColumn({ tags, projects, counts, open, href, scope, reload, onNavigate }: Omit<CustomersColumnInput, "layout" | "onFold" | "tags"> & {
  tags: readonly Tag[] | null; scope?: ReactNode; reload: () => void; onNavigate: Navigate;
}) {
  const sidebar = useCustomersSidebar(tags);
  const [params] = useSearchParams(); const setParam = useSetParam();
  const customizing = params.get("customize") === "1";
  useEffect(() => { if (sidebar.saved) reload(); }, [sidebar.saved]); // eslint-disable-line react-hooks/exhaustive-deps
  const known = tags ?? [];
  const groups = useMemo(() => customersViewGroups({ layout: sidebar.layout, tags: known, projects, counts, open, href, onFold: (id) => sidebar.change(toggleFolded(sidebar.layout, id)) }),
    [sidebar.layout, known, projects, counts, open, href]); // eslint-disable-line react-hooks/exhaustive-deps
  const footer = <Button variant="ghost" size="sm" icon="tune" onClick={() => setParam("customize", "1")}>Customize</Button>;
  return (
    <>
      <ViewsNav groups={groups} scope={scope} footer={footer} empty="Nothing shown. Customize the column." onNavigate={onNavigate} />
      {customizing && <CustomizeSidebar store={sidebar} area={customersArea(known, projects)} onClose={() => setParam("customize", null)} />}
    </>
  );
}
